import {
  baseContainer,
  iconMargin,
  successIcon,
  successText,
  dangerIcon,
  notApplicableText,
  dangerContainer,
  neutralText,
  selectPaymentText,
} from "../styles_vanilla.css";
import {
  notApplicableContainer,
  pendingContainer,
  pendingIcon,
  pendingText,
} from "./styles_vanilla.css";
import { ChangeIndicatorConfig, RiskStatusType, StatusConfig } from "./types";

export const STATUS_CONFIG: Record<RiskStatusType, StatusConfig> = {
  // Low Risk Configs
  [RiskStatusType.LOW_NONE]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-min",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Low risk",
      className: successText,
    },
  },
  [RiskStatusType.LOW_PENDING]: {
    containerClass: `${baseContainer} ${pendingContainer}`,
    icon: {
      type: "gauge-simple-min",
      className: `${iconMargin} ${pendingIcon}`,
    },
    text: {
      content: "Low risk",
      className: pendingText,
    },
  },
  [RiskStatusType.LOW_DECREASE]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-min",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Low risk",
      className: successText,
    },
  },
  [RiskStatusType.LOW_DECREASE_TWO]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-min",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Low risk",
      className: successText,
    },
  },
  [RiskStatusType.LOW_UNCHANGED]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-min",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Low risk",
      className: successText,
    },
  },

  // Medium Risk Configs
  [RiskStatusType.MEDIUM_NONE]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-low",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Medium risk",
      className: successText,
    },
  },
  [RiskStatusType.MEDIUM_DECREASE]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-low",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Medium risk",
      className: successText,
    },
  },
  [RiskStatusType.MEDIUM_INCREASE]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-low",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Medium risk",
      className: successText,
    },
  },
  [RiskStatusType.MEDIUM_UNCHANGED]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-low",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Medium risk",
      className: successText,
    },
  },
  [RiskStatusType.MEDIUM_MANUAL]: {
    containerClass: baseContainer,
    icon: {
      type: "gauge-simple-low",
      className: `${iconMargin} ${successIcon}`,
    },
    text: {
      content: "Medium risk",
      className: successText,
    },
  },

  // High Risk Configs
  [RiskStatusType.HIGH_NONE]: {
    containerClass: `${baseContainer} ${dangerContainer}`,
    icon: {
      type: "gauge-simple-high",
      className: `${iconMargin} ${dangerIcon}`,
    },
    text: {
      content: "High risk",
      className: dangerIcon,
    },
  },
  [RiskStatusType.HIGH_INCREASE]: {
    containerClass: `${baseContainer} ${dangerContainer}`,
    icon: {
      type: "gauge-simple-high",
      className: `${iconMargin} ${dangerIcon}`,
    },
    text: {
      content: "High risk",
      className: dangerIcon,
    },
  },
  [RiskStatusType.HIGH_UNCHANGED]: {
    containerClass: `${baseContainer} ${dangerContainer}`,
    icon: {
      type: "gauge-simple-high",
      className: `${iconMargin} ${dangerIcon}`,
    },
    text: {
      content: "High risk",
      className: dangerIcon,
    },
  },
  [RiskStatusType.HIGH_DECREASE]: {
    containerClass: `${baseContainer} ${dangerContainer}`,
    icon: {
      type: "gauge-simple-high",
      className: `${iconMargin} ${dangerIcon}`,
    },
    text: {
      content: "High risk",
      className: dangerIcon,
    },
  },
  [RiskStatusType.HALT_NONE]: {
    containerClass: `${baseContainer} ${dangerContainer}`,
    icon: {
      type: "gauge-simple-high",
      className: `${iconMargin} ${dangerIcon}`,
    },
    text: {
      content: "Halt risk",
      className: dangerIcon,
    },
  },
  // Not Determined Config
  [RiskStatusType.NOT_DETERMINED]: {
    containerClass: `${baseContainer} ${pendingContainer}`,
    icon: {
      type: "circle",
      className: `${iconMargin} ${pendingIcon}`,
    },
    text: {
      content: "Not determined",
      className: pendingText,
    },
  },
  // Not Applicable Config
  [RiskStatusType.NOT_APPLICABLE]: {
    containerClass: `${baseContainer} ${notApplicableContainer}`,
    text: {
      content: "Not applicable",
      className: notApplicableText,
    },
  },

  [RiskStatusType.SELECT_PAYMENT_BENEFICIARY]: {
    containerClass: baseContainer,
    text: {
      content: "Select payment beneficiary",
      className: `${neutralText} ${selectPaymentText}`,
    },
  },
} as const;

export const CHANGE_CONFIG: Record<RiskStatusType, ChangeIndicatorConfig | null> = {
  // Low Risk Changes
  [RiskStatusType.LOW_NONE]: null,
  [RiskStatusType.LOW_PENDING]: null,
  [RiskStatusType.LOW_DECREASE]: {
    type: "arrow-down",
    count: 1,
  },
  [RiskStatusType.LOW_DECREASE_TWO]: {
    type: "arrow-down",
    count: 2,
  },
  [RiskStatusType.LOW_UNCHANGED]: {
    showDash: true,
  },

  // Medium Risk Changes
  [RiskStatusType.MEDIUM_NONE]: null,
  [RiskStatusType.MEDIUM_DECREASE]: {
    type: "arrow-down",
    count: 1,
  },
  [RiskStatusType.MEDIUM_INCREASE]: {
    type: "arrow-up",
    count: 1,
    isDanger: true,
  },
  [RiskStatusType.MEDIUM_UNCHANGED]: {
    showDash: true,
  },
  [RiskStatusType.MEDIUM_MANUAL]: {
    showManual: true,
  },

  // High Risk Changes
  [RiskStatusType.HIGH_NONE]: null,
  [RiskStatusType.HIGH_INCREASE]: {
    type: "arrow-up",
    count: 1,
    isDanger: true,
  },
  [RiskStatusType.HIGH_UNCHANGED]: {
    showDash: true,
  },
  [RiskStatusType.HIGH_DECREASE]: {
    type: "arrow-down",
    count: 1,
  },
  [RiskStatusType.HALT_NONE]: null,
  // Not Determined Changes
  [RiskStatusType.NOT_DETERMINED]: null,
  // Not Applicable Changes
  [RiskStatusType.NOT_APPLICABLE]: null,
  [RiskStatusType.SELECT_PAYMENT_BENEFICIARY]: null,
} as const;
